import React, { useState } from "react";
import { Link } from "react-router-dom";
import { X, ArrowRight } from "lucide-react";

const JUNETEENTH_IMG =
  "https://customer-assets.emergentagent.com/job_787f84ca-2d17-458c-a9ea-1a3a90b71210/artifacts/056l6w21_262D8476-CAEB-43FE-8D93-D972FF2C6728.png";

// Show through June 22, 2026 (month index 5 = June). Hidden automatically after.
const isJuneteenthWindow = () => new Date() < new Date(2026, 5, 23, 0, 0, 0);

/** Sitewide dismissible Juneteenth ribbon. */
export const JuneteenthBanner = () => {
  const [dismissed, setDismissed] = useState(
    () => typeof window !== "undefined" && localStorage.getItem("juneteenth_2026_dismissed") === "1"
  );

  if (!isJuneteenthWindow() || dismissed) return null;

  const close = () => {
    setDismissed(true);
    try { localStorage.setItem("juneteenth_2026_dismissed", "1"); } catch (e) {}
  };

  return (
    <div className="relative z-[60] bg-slate-950 text-white" data-testid="juneteenth-banner">
      {/* Pan-African accent stripe */}
      <div className="h-1 w-full flex">
        <div className="flex-1 bg-red-600" />
        <div className="flex-1 bg-black" />
        <div className="flex-1 bg-green-600" />
      </div>
      <Link to="/we-refuse-to-be-divided" className="block hover:bg-slate-900 transition-colors">
        <div className="max-w-7xl mx-auto px-4 py-2.5 flex items-center justify-center gap-2 text-center">
          <span className="inline-flex gap-1" aria-hidden>
            <span className="w-2.5 h-2.5 rounded-full bg-red-500" />
            <span className="w-2.5 h-2.5 rounded-full bg-white" />
            <span className="w-2.5 h-2.5 rounded-full bg-green-500" />
          </span>
          <p className="text-xs sm:text-sm font-semibold">
            <span className="text-amber-300 font-bold">Happy Juneteenth — Freedom Day.</span>{" "}
            <span className="hidden sm:inline text-slate-200">Celebrating freedom &amp; the fight for worker justice.</span>
            <span className="inline-flex items-center gap-1 ml-2 text-amber-300 font-bold">
              Learn more <ArrowRight className="w-3.5 h-3.5" />
            </span>
          </p>
        </div>
      </Link>
      <button
        onClick={close}
        data-testid="juneteenth-banner-close"
        aria-label="Dismiss Juneteenth banner"
        className="absolute right-2 top-1/2 -translate-y-1/2 mt-0.5 w-6 h-6 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

/** Homepage Juneteenth spotlight section. */
export const JuneteenthSpotlight = () => {
  if (!isJuneteenthWindow()) return null;

  return (
    <section
      className="bg-gradient-to-br from-amber-500 via-amber-500 to-amber-600 py-12 md:py-16 border-y-4 border-slate-900"
      data-testid="juneteenth-spotlight"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid md:grid-cols-2 gap-8 md:gap-12 items-center">
        <div className="flex justify-center">
          <img
            src={JUNETEENTH_IMG}
            alt="Happy Juneteenth — Freedom Day · Labor Education, Memphis & West TN, Union Strong"
            loading="lazy"
            className="w-full max-w-sm rounded-2xl shadow-2xl border-4 border-white"
            data-testid="juneteenth-spotlight-img"
          />
        </div>
        <div className="text-slate-900">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-slate-900 text-amber-300 text-xs font-bold tracking-widest mb-4">
            JUNE 19 · FREEDOM DAY
          </div>
          <h2 className="font-serif text-3xl md:text-4xl font-bold leading-tight mb-4">
            Happy Juneteenth from Labor Education.
          </h2>
          <p className="text-slate-800 text-base md:text-lg leading-relaxed mb-3">
            Juneteenth marks June 19, 1865 — the day enslaved people in Texas finally learned they were
            free, more than two years after the Emancipation Proclamation. Freedom and labor have always
            been bound together: from the fight to own one&rsquo;s own work, to A. Philip Randolph and the
            Brotherhood of Sleeping Car Porters, to the Memphis sanitation workers who declared{" "}
            <strong>&ldquo;I AM A MAN.&rdquo;</strong>
          </p>
          <p className="text-slate-800 text-base leading-relaxed mb-6">
            We honor that legacy by continuing the work — to educate, organize, and mobilize for every
            worker&rsquo;s dignity.
          </p>
          <div className="flex flex-wrap gap-3">
            <Link
              to="/we-refuse-to-be-divided"
              data-testid="juneteenth-cta-refuse"
              className="inline-flex items-center gap-2 bg-slate-900 hover:bg-slate-800 text-white font-bold px-5 py-3 rounded-lg shadow-lg transition-all"
            >
              We Refuse to Be Divided <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/unions/union_cbtu_memphis"
              data-testid="juneteenth-cta-cbtu"
              className="inline-flex items-center gap-2 bg-white hover:bg-amber-50 text-slate-900 font-bold px-5 py-3 rounded-lg shadow-lg transition-all"
            >
              CBTU Memphis
            </Link>
            <Link
              to="/unions/union_apri_memphis"
              data-testid="juneteenth-cta-apri"
              className="inline-flex items-center gap-2 bg-white hover:bg-amber-50 text-slate-900 font-bold px-5 py-3 rounded-lg shadow-lg transition-all"
            >
              APRI Memphis
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};
